import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NavLink } from "react-router-dom";

import { navGroupsForRoles } from "../nav/navigation";
import type { Role } from "../lib/roles";

/**
 * Боковая панель разделов (CR-5, решение владельца 07.09.2026 — вариант В3 в docs/29 §3.3).
 *
 * ПОЧЕМУ НЕ ВЕРХНЕЕ МЕНЮ. Разделов двенадцать, в строку помещались десять: администратору две
 * вкладки не показывались вовсе, а при открытии «Настроек» меню прокручивалось вправо и прятало
 * начало списка. Раздел, которого не видно, для пользователя не существует. Вертикальный список
 * помещает вдвое больше пунктов и оставляет запас на фазу 2.
 *
 * ОБРАЗЕЦ — ОГЛАВЛЕНИЕ СПРАВКИ (`help/HelpLayout.tsx`): там такая колонка уже работает, включая
 * решённую задачу «длинная страница не утаскивает список за верхний край». Повторяем её приёмы:
 * `sticky`, собственная прокрутка, `no-print`, `aria-current` у текущего пункта.
 *
 * СВЁРНУТОЕ СОСТОЯНИЕ — ЗНАЧКИ, НО НЕ БЕЗЫМЯННЫЕ. Подпись остаётся в разметке классом `sr-only`:
 * её не видно глазами, но её читает программа чтения с экрана, и по ней же ссылку находят тесты.
 * Экранная подсказка (`title`) добавляется отдельно — она для мыши, а не для доступности.
 */
export function NavSidebar({
  roles,
  collapsed,
  onToggle,
}: {
  roles: readonly Role[];
  collapsed: boolean;
  onToggle: () => void;
}) {
  const groups = navGroupsForRoles(roles);
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <aside
      className={`no-print shrink-0 border-r border-border bg-surface ${
        collapsed ? "w-[var(--w-nav-collapsed)]" : "w-[var(--w-nav)]"
      }`}
    >
      {/* Своя прокрутка: длинный реестр справа не должен утаскивать разделы за верхний край. */}
      <div className="sticky top-0 flex h-full max-h-screen flex-col overflow-y-auto py-3">
        <nav id="nav-sections" aria-label="Разделы" className="flex-1 px-2">
          {groups.map((group) => (
            <div key={group.id} className="mb-3">
              {collapsed ? (
                // В свёрнутом виде заголовок группы превращается в разделитель: место под текст
                // есть только у значков, а группы всё равно надо отличать друг от друга.
                <div className="mx-1 mb-1.5 border-t border-border-soft" aria-hidden="true" />
              ) : (
                <p className="mb-1.5 px-2 text-[12px] font-bold uppercase tracking-wide text-muted">
                  {group.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.id}>
                    <NavLink
                      to={item.path}
                      title={collapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        [
                          "flex items-center gap-2 rounded-[var(--radius-m)] px-2 py-1.5 text-[13px]",
                          collapsed ? "justify-center" : "",
                          isActive ? "bg-control-bg font-bold text-accent" : "text-ink hover:bg-bg",
                        ].join(" ")
                      }
                    >
                      <item.icon size={16} aria-hidden="true" className="shrink-0" />
                      <span className={collapsed ? "sr-only" : ""}>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Кнопка сворачивания — внизу, чтобы не спорить за верх с первым разделом. Видна в
            обоих состояниях: из свёрнутого всегда есть выход (тупиков нет). */}
        <div className="mt-2 border-t border-border-soft px-2 pt-2">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-controls="nav-sections"
            title={collapsed ? "Развернуть панель" : "Свернуть панель"}
            className={`flex w-full items-center gap-2 rounded-[var(--radius-m)] px-2 py-1.5 text-[13px] text-muted hover:bg-bg hover:text-ink ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <ToggleIcon size={16} aria-hidden="true" className="shrink-0" />
            <span className={collapsed ? "sr-only" : ""}>
              {collapsed ? "Развернуть панель" : "Свернуть панель"}
            </span>
          </button>
        </div>
      </div>
    </aside>
  );
}
